import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Quote } from 'lucide-react';
import { PeerPoll, PollOption } from '../components/PeerPoll';
interface Props {
  heroImage?: string;
  heroVideo?: string;
  pollOptions: PollOption[];
  userChoice?: string;
  quoteSource: string;
  quoteText: string;
  supportingText: string;
  onContinue: () => void;
}
export function SlideReward({
  heroImage,
  heroVideo,
  pollOptions,
  userChoice,
  quoteSource,
  quoteText,
  supportingText,
  onContinue
}: Props) {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.5,
    ease: 'easeOut'
  }} className="flex-1 flex flex-col relative overflow-hidden" data-id="element-549">
      {/* 1. Premium animated visual */}
      <div className="relative h-[32%] overflow-hidden shrink-0" data-id="element-550">
        {heroVideo ? <motion.video src={heroVideo} autoPlay loop muted playsInline initial={{
        scale: 1.15,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 2.5,
        ease: 'easeOut'
      }} className="absolute inset-0 w-full h-full object-cover" data-id="element-551" /> : heroImage ? <motion.img src={heroImage} alt="" initial={{
        scale: 1.15,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 2.5,
        ease: 'easeOut'
      }} className="absolute inset-0 w-full h-full object-cover" data-id="element-552" /> : null}

        {/* Soft light overlays for brand mood */}
        <div className="absolute inset-0 bg-gradient-to-b from-minerva-blue/10 via-transparent to-[#F6FAFD]" data-id="element-553" />

        {/* Animated scanning line for biotech feel */}
        <motion.div className="absolute left-0 right-0 h-[2px] bg-minerva-blue/40 blur-[1px]" initial={{
        top: 0,
        opacity: 0
      }} animate={{
        top: '100%',
        opacity: [0, 0.6, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-554" />
      </div>

      {/* Content area */}
      <div className="flex-1 flex flex-col px-5 pt-4 pb-24 overflow-y-auto gap-6" data-id="element-555">
        {/* 2. Peer comparison reward */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_8px_24px_-12px_rgba(0,80,114,0.15)] shrink-0" data-id="element-556">
          <PeerPoll options={pollOptions} userChoice={userChoice} data-id="element-557" />
        </motion.div>

        {/* 3 & 4. Industry insight quote + Supporting context */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="flex flex-col gap-3 px-2 shrink-0" data-id="element-558">
          <div className="flex items-center gap-2" data-id="element-559">
            <Quote size={14} className="text-minerva-cyan" data-id="element-560" />
            <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase" data-id="element-561">
              {quoteSource}
            </span>
          </div>

          <blockquote className="text-[22px] font-display font-bold text-slate-900 leading-snug" data-id="element-562">
            "{quoteText}"
          </blockquote>

          <p className="text-[13px] font-medium text-slate-500 leading-relaxed" data-id="element-563">
            {supportingText}
          </p>
        </motion.div>

        {/* 5. CTA button */}
        <motion.button initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.9
      }} whileTap={{
        scale: 0.97
      }} onClick={onContinue} className="mt-4 w-full h-14 rounded-2xl bg-minerva-blue text-white font-semibold flex items-center justify-center gap-2 shadow-[0_8px_24px_-6px_rgba(0,133,189,0.4)] hover:bg-minerva-dark transition-colors shrink-0" data-id="element-564">
          Continue
          <ArrowRight size={18} data-id="element-565" />
        </motion.button>
      </div>
    </motion.div>;
}